import "@patron/patron-css/patron/index.css";
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

// Patron UI components
import { Heading } from "@patron/patron-react/heading";
import { Label } from "@patron/patron-react/label";
import { Radio } from "@patron/patron-react/radio";
import { Notification } from "@patron/patron-react/notification";

// External packages
import base64 from "react-native-base64";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router";

// Application components
import Spinner from "../../spinner/Spinner";
import EMMManaged from "./EMMManaged";
import CustomerManaged from "./CustomerManaged";

// Constants and utilities
import * as serverConstants from "../../constants/ServerConstants";
import * as constants from "../../constants/Constants";
import * as error from "../../constants/ErrorCodes";
import * as cacheUtil from "../../util/CacheUtility";
import * as ErrorUtil from "../../util/ErrorUtility";
import { parseEnterpriseDetails } from "./models";

// Type definitions
import { EnterpriseDetailsPropType } from "./propTypes";

// Styles
import "./EnterpriseRegistration.scss";

const EnterpriseRegistration = ({
  enterpriseResponse,
  showOrHideButton,
  getEnterPriseRegDetails,
}) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({
    showSpinner: false,
    showError: false,
    errorMsg: false,
    showSuccess: false,
    successMsg: "",
    isAuthenticated: true,
    registrationType: constants.ENTERPRISE_MANAGED_TYPE, // 'emm' or 'cme'
    enterpriseDisplayName: "",
    dataProtectionOfficerName: "",
    dataProtectionOfficerEmail: "",
    dataProtectionOfficerPhone: "",
    euRepresentativeName: "",
    euRepresentativeEmail: "",
    euRepresentativePhone: "",
    contactEmail: "",
    termsAndConditions: false,
    validDataProtectionOfficerEmail: false,
    validEuRepresentativeEmail: false,
    validContactEmail: false,
    validDataProtectionOfficerPhone: false,
    validEuRepresentativePhone: false,
    enterpriseToken: null,
  });

  const updateFormData = (field, value) => {
    setFormState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const updateFormValidity = (field, value) => {
    updateFormData(field, value);
  };

  const handleRegistrationTypeChange = (type) => {
    const termsAndConditions = type === constants.CUSTOMER_MANAGED_TYPE;

    setFormState((prevState) => ({
      ...prevState,
      registrationType: type,
      termsAndConditions,
    }));
  };

  // Helper function to extract contact info from enterprise details
  const extractContactInfo = useCallback((contactInfo) => {
    if (!contactInfo) {
      return {};
    }
    
    return {
      dataProtectionOfficerName: contactInfo.dataProtectionOfficerName || "",
      dataProtectionOfficerEmail: contactInfo.dataProtectionOfficerEmail || "",
      dataProtectionOfficerPhone: contactInfo.dataProtectionOfficerPhone || "",
      euRepresentativeName: contactInfo.euRepresentativeName || "",
      euRepresentativeEmail: contactInfo.euRepresentativeEmail || "",
      euRepresentativePhone: contactInfo.euRepresentativePhone || "",
      contactEmail: contactInfo.contactEmail || ""
    };
  }, []);
  
  // Helper function to determine registration type from enterprise details
  const determineRegistrationType = useCallback((enterpriseDetails) => {
    if (enterpriseDetails?.managedGoogleDomainType === constants.CME_MANAGED_DOMAIN_TYPE) {
      return constants.CUSTOMER_MANAGED_TYPE;
    }
    return null; // Return null if no change needed
  }, []);

  // Helper function to update form state based on enterprise response
  const getUpdatedFormState = useCallback((prevState, enterpriseResponse) => {
    let updatedState = { ...prevState };
    
    // Update display name if available
    if (enterpriseResponse.enterpriseDisplayName) {
      updatedState.enterpriseDisplayName = enterpriseResponse.enterpriseDisplayName;
    }

    // Parse and process enterprise details if available
    if (enterpriseResponse.enterpriseDetails) {
      const enterpriseDetails = parseEnterpriseDetails(enterpriseResponse);
      
      // Update registration type if needed
      const newType = determineRegistrationType(enterpriseDetails);
      if (newType) {
        updatedState.registrationType = newType;
      }
      
      // Extract and apply contact info if available
      if (enterpriseDetails?.contactInfo) {
        const contactInfoUpdates = extractContactInfo(enterpriseDetails.contactInfo);
        updatedState = { ...updatedState, ...contactInfoUpdates };
      }
    }
    
    return updatedState;
  }, [determineRegistrationType, extractContactInfo]);

  const setEnterpriseRegDetails = useCallback(() => {
    setFormState(prevState => getUpdatedFormState(prevState, enterpriseResponse));
  }, [enterpriseResponse, getUpdatedFormState]);

  useEffect(() => {
    if (enterpriseResponse.enterpriseId !== "") {
      setEnterpriseRegDetails();
    }
  }, [enterpriseResponse, setEnterpriseRegDetails]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const enterpriseToken = urlParams.get("enterpriseToken");

    if (enterpriseToken) {
      setFormState((prevState) => ({
        ...prevState,
        registrationType: constants.CUSTOMER_MANAGED_TYPE,
        enterpriseToken: enterpriseToken,
      }));
    }
  }, []);

  const registerEnterprise = (method, customFormData = null) => {
    setFormState((prevState) => ({
      ...prevState,
      showSpinner: true,
      showSuccess: false,
      showError: false,
    }));

    window.scroll({ top: 0, behavior: "smooth" });
    fetch(window.location.origin + serverConstants.ADMIN_COMMAND_URL, {
      method: "POST",
      headers: {
        Authorization: cacheUtil.readFromCache(serverConstants.BFCONFIG_SID),
      },
      body: getReqBody(method, customFormData),
    })
      .then((response) => {
        if (
          response.status === error.HTTP_UNAUTHORIZED ||
          response.status === error.HTTP_FORBIDDEN
        ) {
          handleUnauthorizedResponse();
        } else if (!response.ok) {
          setFormState((prevState) => ({
            ...prevState,
            showSpinner: false,
            showError: true,
            errorMsg: ErrorUtil.transactionErrorDetails(response, t),
          }));
        } else return response.json();
      })
      .then((response) => {
        if (response) {
          getEnterPriseRegDetails();
          const successMsg =
            method === constants.HTTP_POST
              ? t("enterpriseRegisterSuccessMsg")
              : t("enterpriseUpdateSuccessMsg");

          setFormState((prevState) => ({
            ...prevState,
            showSuccess: true,
            successMsg,
            showSpinner: false,
          }));
        }
      })
      .catch((error) => {
        setFormState((prevState) => ({
          ...prevState,
          showSpinner: false,
          showError: true,
          errorMsg: error,
        }));
      });
  };

  const handleUnauthorizedResponse = () => {
    cacheUtil.clearCacheItem(serverConstants.BFCONFIG_SID);
    setFormState((prevState) => ({
      ...prevState,
      showSpinner: false,
      isAuthenticated: false,
    }));
    return false;
  };

  // Helper function to create contact info object from data
  const createContactInfo = (data) => {
    return {
      dataProtectionOfficerName: data.dataProtectionOfficerName || "",
      dataProtectionOfficerEmail: data.dataProtectionOfficerEmail || "",
      dataProtectionOfficerPhone: data.dataProtectionOfficerPhone || "",
      euRepresentativeName: data.euRepresentativeName || "",
      euRepresentativeEmail: data.euRepresentativeEmail || "",
      euRepresentativePhone: data.euRepresentativePhone || "",
      contactEmail: data.contactEmail || "",
    };
  };

  // Helper function to check if display name is valid
  const getDisplayName = (name) => {
    return name && name.trim() !== "" ? name : "";
  };

  const getPayLoad = (submissionData = null) => {
    if (submissionData && submissionData.enterprise_name === constants.CUSTOMER_MANAGED_TYPE) {
      const payload = {};
      const displayName = getDisplayName(submissionData.enterpriseDisplayName);
      if (displayName) {
        payload.enterpriseDisplayName = displayName;
      }
      return payload;
    }
    // For regular form state processing - Customer Managed
    if (formState.registrationType === constants.CUSTOMER_MANAGED_TYPE) {
      const payload = {};
      const displayName = getDisplayName(formState.enterpriseDisplayName);
      if (displayName) {
        payload.enterpriseDisplayName = displayName;
      }
      return payload;
    }
    // For EMM managed, we need the full payload with required fields
    return {
      contactInfo: createContactInfo(formState),
      enterpriseDisplayName: formState.enterpriseDisplayName,
    };
  };

  const getReqBody = (method, customFormData = null) => {
    const payload = getPayLoad(customFormData);
    
    // Check if this is a customer managed registration
    const isCustomerManaged =
      customFormData?.enterprise_name === constants.CUSTOMER_MANAGED_TYPE ||
      (customFormData === null &&
        formState.registrationType === constants.CUSTOMER_MANAGED_TYPE);

    // Select the appropriate request body creator based on registration type and method
    if (isCustomerManaged) {
      return getCustomerManagedRequestBody(method, payload, customFormData);
    } else {
      return getEnterpriseManageRequestBody(method, payload);
    }
  };

  // Helper to create request body for customer managed registration
  const getCustomerManagedRequestBody = (method, payload, customFormData) => {
    if (constants.HTTP_POST === method) {
      const body = getCMEPostRequestBody(payload, customFormData);
      return body;
    } else if (constants.HTTP_PATCH === method) {
      const body = getCMEPatchRequestBody(payload);
      return body;
    }
    return null;
  };

  // Helper to create request body for enterprise managed registration
  const getEnterpriseManageRequestBody = (method, payload) => {
    if (constants.HTTP_PATCH === method) {
      const body = JSON.stringify({
        path_param: "enterprises",
        management_type: constants.ENTERPRISE_MANAGED_TYPE,
        target: "mdm",
        payload: base64.encode(JSON.stringify(payload)),
        requestType: constants.HTTP_PATCH,
        query_param: {
          updateMask: constants.CONTACT_INFO + "," + constants.ENTERPRISE_DISPLAY_NAME,
        },
      });
      return body;
    } else {
      return JSON.stringify({
        path_param: "enterprises",
        management_type: constants.ENTERPRISE_MANAGED_TYPE,
        target: "mdm",
        payload: base64.encode(JSON.stringify(payload)),
        query_param: { agreementAccepted: "true" },
      });
    }
  };

  const getCMEPostRequestBody = (payload, customFormData = null) => {
    const signupUrlName = customFormData?.signupurl_name || "";
    const enterpriseToken =
      customFormData?.enterprise_token || formState.enterpriseToken || "";

    return JSON.stringify({
      path_param: "enterprises",
      management_type: constants.CUSTOMER_MANAGED_TYPE, // Customer Managed type
      target: "mdm",
      payload: base64.encode(JSON.stringify(payload)),
      query_param: {
        signupUrlName: signupUrlName,
        enterpriseToken: enterpriseToken,
      },
    });
  };

  const getCMEPatchRequestBody = (payload) => {
    return JSON.stringify({
      path_param: "enterprises",
      management_type: constants.CUSTOMER_MANAGED_TYPE, // Customer Managed type
      target: "mdm",
      payload: base64.encode(JSON.stringify(payload)),
      requestType: constants.HTTP_PATCH,
      query_param: {
        updateMask: constants.ENTERPRISE_DISPLAY_NAME,
      },
    });
  };

  // Phone number validation moved to form components

  return (
    <React.Fragment>
      {formState.showSpinner && <Spinner />}
      {!formState.isAuthenticated && <Redirect to="/config?session=false" />}
      <div className="hcl-container">
        <Notification
          onClose={() =>
            setFormState((prevState) => ({ ...prevState, showError: false }))
          }
          title={formState.errorMsg}
          type="danger"
          visible={formState.showError}
        />
        <Notification
          onClose={() =>
            setFormState((prevState) => ({ ...prevState, showSuccess: false }))
          }
          title={formState.successMsg}
          type="success"
          visible={formState.showSuccess}
        />
        {enterpriseResponse.enterpriseId === "" && (
          <div>
            <div className="mt-4">
              <Heading type="h4">{t("registerManagedGooglePlay")}</Heading>
            </div>
            <Label className="mt-5">{t("registerManagedGooglePlayDesc")}</Label>

            <div className="mt-4 radio-group-row">
              <div className="hcl-col-4">
                <Radio
                  checked={
                    formState.registrationType ===
                    constants.ENTERPRISE_MANAGED_TYPE
                  }
                  value={constants.ENTERPRISE_MANAGED_TYPE}
                  id="emm-managed"
                  labelText={t("emmManaged") || "EMM Managed"}
                  name="registration-type"
                  onChange={() =>
                    handleRegistrationTypeChange(
                      constants.ENTERPRISE_MANAGED_TYPE
                    )
                  }
                />
              </div>
              <div className="hcl-col-4">
                <Radio
                  checked={
                    formState.registrationType ===
                    constants.CUSTOMER_MANAGED_TYPE
                  }
                  id="customer-managed"
                  value={constants.CUSTOMER_MANAGED_TYPE}
                  labelText={t("customerManaged") || "Customer Managed"}
                  name="registration-type"
                  onChange={() =>
                    handleRegistrationTypeChange(
                      constants.CUSTOMER_MANAGED_TYPE
                    )
                  }
                />
              </div>
            </div>
          </div>
        )}

        {enterpriseResponse.enterpriseId !== "" && (
          <div>
            <div className="mt-4">
              <Heading type="h5">
                {t("registeredManagedGooglePlayAccounts")}
              </Heading>
            </div>
          </div>
        )}

        <form>
          {formState.registrationType === constants.ENTERPRISE_MANAGED_TYPE && (
            <EMMManaged
              enterpriseResponse={enterpriseResponse}
              updateFormData={updateFormData}
              updateFormValidity={updateFormValidity}
              showSpinner={formState.showSpinner}
              showOrHideButton={showOrHideButton}
              registerEnterprise={registerEnterprise}
            />
          )}

          {formState.registrationType === constants.CUSTOMER_MANAGED_TYPE && (
            <CustomerManaged
              enterpriseResponse={enterpriseResponse}
              showOrHideButton={showOrHideButton}
              enterpriseToken={formState.enterpriseToken}
              registerEnterprise={registerEnterprise}
            />
          )}

          {!enterpriseResponse.hasToken &&
            enterpriseResponse.enterpriseId === "" && (
              <div className="hcl-type-zeta mt-3">
                { formState.registrationType === constants.CUSTOMER_MANAGED_TYPE ? t("invalidTokenForCME") : t("invalidTokenForEnterpriseMsg")}
              </div>
            )}
        </form>
      </div>
    </React.Fragment>
  );
};

EnterpriseRegistration.propTypes = {
  enterpriseResponse: PropTypes.shape({
    enterpriseId: PropTypes.string.isRequired,
    serviceAccount: PropTypes.string,
    hasToken: PropTypes.bool,
    accountType: PropTypes.string,
    enterpriseDetails: PropTypes.oneOfType([
      PropTypes.string,
      EnterpriseDetailsPropType,
    ]),
    enterpriseDisplayName: PropTypes.string,
    enterpriseType: PropTypes.string,
  }),
  showOrHideButton: PropTypes.bool,
  getEnterPriseRegDetails: PropTypes.func,
};

export default EnterpriseRegistration;
